import type { ProductStatus } from '~/schemas/shopify/admin.types';
import type { BadgeProps } from '@shopify/polaris';
import type { IPickPageMode } from '../types';

import { EOrderDirectionType } from '~/core/gemxql/types/order-by';
import { THUMB_SHOPIFY } from '~/constants/thumbnail';
import { PICK_PAGE_MODE } from './pickPageMode';
import { IPickPageType } from '../types';

export const DEFAULT_PICK_PAGE_SORT = EOrderDirectionType.DESC;

// What ResourceList passes to `onSelectionChange` for "select all" instead of a list of ids. Polaris
// exports the same sentinel for IndexTable only, so the literal is named here.
export const PICK_PAGE_SELECT_ALL = 'All';

// Rendered as a class on the modal body so ModalPickPage.scss can find this dialog and pin its
// height -- keep in sync with `$modal-pick-page-identifier` there.
export const MODAL_PICK_PAGE_IDENTIFIER = 'modal-pick-page-identifier';

/**
 * The path segment a storefront URL names its page type with, e.g. `/pages/<handle>`. Landing pages
 * are Shopify's "pages", and a blog post is nested under the blog that holds it.
 */
export const PICK_PAGE_TYPE_BY_PATH_SEGMENT: Record<string, IPickPageType> = {
  pages: IPickPageType.LANDING,
  products: IPickPageType.PRODUCT,
  collections: IPickPageType.COLLECTION,
  blogs: IPickPageType.BLOG_POST,
};

// Every row here is a Shopify storefront page, so one without a screenshot falls back to the same
// Shopify thumb the template list uses.
export const PICK_PAGE_THUMBNAIL_FALLBACK = THUMB_SHOPIFY;

export const PICK_PAGE_PAGE_SIZE = 50;

// The homepage has no record behind it, so the row it renders carries a fixed id instead.
export const PICK_PAGE_HOME_ID = 'home';

// `/pages/about` and `/pages/about/` are the same page, so the slash is dropped before either is matched.
export const PICK_PAGE_PATH_TRAILING_SLASH_PATTERN = /\/+$/;

// The path every storefront serves its homepage on, and what a path reduced to nothing reads as.
export const PICK_PAGE_HOME_PATH = '/';

export const PICK_PAGE_SEARCH_DEBOUNCE_MS = 300;

// Newly added or edited pages surface first, whatever the search term is. Each page type spells its
// own sort key enum, but all four name this one, so a single literal types against every listing.
export const PICK_PAGE_SORT_KEY = 'UPDATED_AT' as const;

// Only the product tab offers a sort toggle; the other listings stay newest-first.
export const PICK_PAGE_IS_SORT_REVERSED = true;

// Shopify indexes alphanumeric tokens only, so any punctuation — the standalone `-` and the `™` in
// "Puropet™ - 12 Meses" — can never match and would zero out the whole AND-ed query. Splitting on it
// rather than stripping it keeps a hyphenated handle searchable: `gift-card` becomes `gift` AND
// `card`, both of which still substring-match the handle, whereas `giftcard` would match nothing.
export const PICK_PAGE_SEARCH_TERM_SEPARATOR_PATTERN = /[^\p{L}\p{N}]+/u;

export const PICK_PAGE_STATUS_BADGE: Record<
  ProductStatus,
  { label: string; tone: BadgeProps['tone'] }
> = {
  ACTIVE: { label: 'Active', tone: 'success' },
  DRAFT: { label: 'Draft', tone: undefined },
  ARCHIVED: { label: 'Archived', tone: 'warning' },
  UNLISTED: { label: 'Unlisted', tone: undefined },
};

/**
 * Statuses a product may carry to be picked, by what the modal picks and which side it is for.
 * DRAFT and ARCHIVED never qualify either way: neither leaves the product a storefront page at all.
 */
export const PICK_PAGE_QUERYABLE_STATUSES: Record<
  IPickPageMode,
  Record<'control' | 'variant', ProductStatus[]>
> = {
  // A price test changes what the storefront charges, so its Control has to be a product shoppers
  // already reach; its Variant isn't published yet, so one reachable by direct link alone qualifies.
  [PICK_PAGE_MODE.PRICE]: { control: ['ACTIVE'], variant: ['ACTIVE', 'UNLISTED'] },
  // A URL test only redirects traffic to a page it never publishes itself, so an UNLISTED product
  // serves either side.
  [PICK_PAGE_MODE.URL]: { control: ['ACTIVE', 'UNLISTED'], variant: ['ACTIVE', 'UNLISTED'] },
};

/** Shopify admin section holding the record behind each page type. HOME has none. */
export const PICK_PAGE_ADMIN_SECTION: Record<IPickPageType, string> = {
  [IPickPageType.HOME]: '',
  [IPickPageType.LANDING]: 'pages',
  [IPickPageType.PRODUCT]: 'products',
  [IPickPageType.COLLECTION]: 'collections',
  // Blog posts sit under Content in the Shopify admin, unlike the other three.
  [IPickPageType.BLOG_POST]: 'content/articles',
};

/**
 * What an unavailable page offers to open, by page type — a product or collection is excluded from
 * the Online Store sales channel, while a page or post is hidden by its own visibility. HOME is
 * always served, so it never reads this.
 */
export const PICK_PAGE_EDIT_ADMIN_LABEL: Record<IPickPageType, string> = {
  [IPickPageType.HOME]: '',
  [IPickPageType.LANDING]: 'Edit visibility',
  [IPickPageType.PRODUCT]: 'Edit product',
  [IPickPageType.COLLECTION]: 'Edit collection',
  [IPickPageType.BLOG_POST]: 'Edit visibility',
};

/**
 * Why an unavailable page can't be selected, by page type — a product and a collection are excluded
 * from the Online Store sales channel, while a page and a blog post are hidden by their own
 * visibility. HOME is always served, so it never reads this.
 */
export const PICK_PAGE_NOT_AVAILABLE_TOOLTIP: Record<IPickPageType, string> = {
  [IPickPageType.HOME]: '',
  [IPickPageType.LANDING]:
    'This page is hidden from your Online Store. Change its visibility to select it.',
  [IPickPageType.PRODUCT]:
    'Publish this product to the Online Store sales channel in Shopify to select.',
  [IPickPageType.COLLECTION]:
    'Publish this collection to the Online Store sales channel in Shopify to select.',
  [IPickPageType.BLOG_POST]:
    'This blog post is hidden from your Online Store. Change its visibility to select it.',
};
